import { useEffect, useState } from "react";
import { XIcon } from "@heroicons/react/outline";
import { CURRENCIES, serviceKeys } from "../../constants";
import {
  Autocomplete,
  GoogleMap,
  LoadScript,
  Marker,
} from "@react-google-maps/api";

const libraries = ["places"];

const EventFormModal = ({
  isOpen,
  onClose,
  onSubmit,
  loading,
  mode = "create",
  initialData = null,
}) => {
  const [form, setForm] = useState({
    name: "",
    date_start: "",
    time_start: "",
    date_end: "",
    time_end: "",
    flyer_portrait: "",
    flyer_horizontal: "",
    map_address: "",
    map_latitude: "",
    map_longitude: "",
    // sold_tickets: 0,
    // invitations_sent: 0,
    // refundable_tickets: 0,
    // amount: 0,
    // currency: "USD",
  });

  const [error, setError] = useState("");

  const [autocomplete, setAutocomplete] = useState(null);

  const center = {
    lat: Number(form.map_latitude) || 40.416775, // default madrid
    lng: Number(form.map_longitude) || -3.70379,
  };

  const handleChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setError("");
  };

  const handleSubmit = () => {
    if (
      !form.name ||
      !form.date_start ||
      !form.date_end ||
      !form.flyer_portrait ||
      !form.flyer_horizontal ||
      !form.map_address
    ) {
      setError("Please fill all the above fields.");
      return;
    }

    const start = `${form.date_start}T${form.time_start || "00:00"}:00`;
    const end = `${form.date_end}T${form.time_end || "00:00"}:00`;

    if (new Date(end) < new Date(start)) {
      setError("End date/time cannot be before start.");
      return;
    }

    onSubmit({
      ...form,
      date_start: start,
      date_end: end,
    });
  };

  useEffect(() => {
    if (!isOpen) return;

    if (initialData) {
      const start = initialData.date_start?.split("T") || [];
      const end = initialData.date_end?.split("T") || [];

      setForm({
        name: initialData.name || "",
        date_start: start[0] || "",
        time_start: start[1]?.slice(0, 5) || "",
        date_end: end[0] || "",
        time_end: end[1]?.slice(0, 5) || "",
        flyer_portrait: initialData.flyer_portrait || "",
        flyer_horizontal: initialData.flyer_horizontal || "",
        map_address: initialData.map_address || "",
        map_latitude: initialData.map_latitude || "",
        map_longitude: initialData.map_longitude || "",
        // sold_tickets: initialData.sold_tickets || 0,
        // invitations_sent: initialData.invitations_sent || 0,
        // refundable_tickets: initialData.refundable_tickets || 0,
        // amount: initialData.amount || 0,
        // currency: initialData.currency || "USD",
      });
    } else {
      setForm({
        name: "",
        date_start: "",
        time_start: "",
        date_end: "",
        time_end: "",
        flyer_horizontal: "",
        flyer_portrait: "",
        map_address: "",
        map_latitude: "",
        map_longitude: "",
        // sold_tickets: 0,
        // invitations_sent: 0,
        // refundable_tickets: 0,
        // amount: 0,
        // currency: "USD",
      });
    }

    setError("");
  }, [isOpen, initialData]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
      <div className="w-full max-w-2xl bg-slate-900 border border-white/10 rounded-xl shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
          <h2 className="text-lg font-semibold text-white">
            {mode === "edit" ? "Edit Event" : "Create Event"}
          </h2>

          <button onClick={onClose} className="text-slate-400 hover:text-white">
            <XIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-6 space-y-5 max-h-[70vh] overflow-y-auto">
          {/* Name */}
          <div>
            <label className="block text-sm text-slate-300 mb-1">
              Event Name
            </label>
            <input
              type="text"
              value={form.name}
              placeholder="Event Name"
              onChange={(e) => handleChange("name", e.target.value)}
              className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-pink-500/40"
            />
          </div>

          <LoadScript
            googleMapsApiKey={serviceKeys.API_KEY}
            libraries={libraries}
          >
            <div>
              <label className="block text-sm text-slate-300 mb-1">
                Venue Address
              </label>
              <p className="block text-xs text-slate-300 mb-1">
                {form.map_address}
              </p>
              <Autocomplete
                onLoad={(auto) => setAutocomplete(auto)}
                onPlaceChanged={() => {
                  if (autocomplete) {
                    const place = autocomplete.getPlace();

                    const lat = place.geometry.location.lat();
                    const lng = place.geometry.location.lng();
                    const address = place.formatted_address;

                    setForm({
                      ...form,
                      map_address: address,
                      map_latitude: lat,
                      map_longitude: lng,
                    });
                  }
                }}
              >
                <input
                  type="text"
                  placeholder="Search location"
                  className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 mb-2 text-sm text-white"
                />
              </Autocomplete>

              <GoogleMap
                mapContainerStyle={{ width: "100%", height: "300px" }}
                center={center}
                zoom={12}
                onClick={(e) => {
                  const lat = e.latLng.lat();
                  const lng = e.latLng.lng();

                  setForm({ ...form, map_latitude: lat, map_longitude: lng });
                }}
              >
                {form.map_latitude && form.map_longitude && (
                  <Marker
                    position={{
                      lat: Number(form.map_latitude),
                      lng: Number(form.map_longitude),
                    }}
                    draggable
                    onDragEnd={(e) => {
                      const lat = e.latLng.lat();
                      const lng = e.latLng.lng();

                      setForm({
                        ...form,
                        map_latitude: lat,
                        map_longitude: lng,
                      });
                    }}
                  />
                )}
              </GoogleMap>
            </div>
          </LoadScript>

          <div className="grid grid-cols-2 gap-4">
            {/* Start */}
            <div>
              <label className="block text-sm text-slate-300 mb-1">
                Start Date & Time
              </label>
              <div className="grid grid-cols-2 gap-2">
                <input
                  type="date"
                  value={form.date_start}
                  onChange={(e) => handleChange("date_start", e.target.value)}
                  className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white"
                />
                <input
                  type="time"
                  value={form.time_start}
                  onChange={(e) => handleChange("time_start", e.target.value)}
                  className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white"
                />
              </div>
            </div>

            {/* End */}
            <div>
              <label className="block text-sm text-slate-300 mb-1">
                End Date & Time
              </label>
              <div className="grid grid-cols-2 gap-2">
                <input
                  type="date"
                  value={form.date_end}
                  onChange={(e) => handleChange("date_end", e.target.value)}
                  className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white"
                />
                <input
                  type="time"
                  value={form.time_end}
                  onChange={(e) => handleChange("time_end", e.target.value)}
                  className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white"
                />
              </div>
            </div>
          </div>

          {/* Poster */}
          {/* Poster Upload */}

          <div>
            <label className="block text-sm text-slate-300 mb-1">
              Event Flyer - Portrait Mode (1080x1920) | (jpg, jpeg, png)
            </label>

            <input
              type="file"
              accept="image/jpeg,image/jpg,image/png"
              onChange={(e) => {
                const file = e.target.files[0];
                if (!file) return;

                const resetInput = () => {
                  e.target.value = null;
                };

                if (
                  !["image/jpeg", "image/jpg", "image/png"].includes(file.type)
                ) {
                  setError("Only JPG, JPEG and PNG files are allowed.");
                  resetInput();
                  return;
                }

                const img = new Image();
                const objectUrl = URL.createObjectURL(file);

                img.onload = () => {
                  const { width, height } = img;

                  if (width !== 1080 || height !== 1920) {
                    handleChange("flyer_portrait", null);
                    setError("Image must be exactly 1080 x 1920.");
                    resetInput();
                    URL.revokeObjectURL(objectUrl);
                    return;
                  }

                  setError(null);
                  handleChange("flyer_portrait", file);
                  URL.revokeObjectURL(objectUrl);
                };

                img.onerror = () => {
                  setError("Invalid image file.");
                  resetInput();
                  URL.revokeObjectURL(objectUrl);
                };

                img.src = objectUrl;
              }}
              className="w-full text-sm text-slate-400"
            />

            {/* Preview */}
            {form.flyer_portrait && (
              <div className="mt-3">
                <img
                  src={
                    typeof form.flyer_portrait === "string"
                      ? form.flyer_portrait
                      : URL.createObjectURL(form.flyer_portrait)
                  }
                  alt="Flyer preview"
                  className="w-32 h-32 object-cover rounded-lg border border-white/10"
                />
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm text-slate-300 mb-1">
              Event Flyer - Horizontal Mode (1920x1080) | (jpg, jpeg, png)
            </label>

            <input
              type="file"
              accept="image/jpeg,image/jpg,image/png"
              onChange={(e) => {
                const file = e.target.files[0];
                if (!file) return;

                const resetInput = () => {
                  e.target.value = null;
                };

                if (
                  !["image/jpeg", "image/jpg", "image/png"].includes(file.type)
                ) {
                  setError("Only JPG, JPEG and PNG files are allowed.");
                  resetInput();
                  return;
                }

                const img = new Image();
                const objectUrl = URL.createObjectURL(file);

                img.onload = () => {
                  const { width, height } = img;

                  if (width !== 1920 || height !== 1080) {
                    handleChange("flyer_horizontal", null);
                    setError("Image must be exactly 1920 x 1080.");
                    resetInput();
                    URL.revokeObjectURL(objectUrl);
                    return;
                  }

                  // valid
                  setError(null);
                  handleChange("flyer_horizontal", file);
                  URL.revokeObjectURL(objectUrl);
                };

                img.onerror = () => {
                  setError("Invalid image file.");
                  resetInput();
                  URL.revokeObjectURL(objectUrl);
                };

                img.src = objectUrl;
              }}
              className="w-full text-sm text-slate-400"
            />

            {/* Preview */}
            {form.flyer_horizontal && (
              <div className="mt-3">
                <img
                  src={
                    typeof form.flyer_horizontal === "string"
                      ? form.flyer_horizontal
                      : URL.createObjectURL(form.flyer_horizontal)
                  }
                  alt="Flyer preview"
                  className="w-32 h-32 object-cover rounded-lg border border-white/10"
                />
              </div>
            )}
          </div>

          {/* Tickets */}
          {/* <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-slate-300 mb-1">
                Sold Tickets
              </label>
              <input
                type="number"
                placeholder="Sold tickets"
                value={form.sold_tickets}
                onChange={(e) =>
                  handleChange("sold_tickets", Number(e.target.value))
                }
                className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white"
              />
            </div>

            <div>
              <label className="block text-sm text-slate-300 mb-1">
                Refundable tickets
              </label>
              <input
                type="number"
                placeholder="Refundable tickets"
                value={form.refundable_tickets}
                onChange={(e) =>
                  handleChange("refundable_tickets", Number(e.target.value))
                }
                className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-slate-300 mb-1">
                Invitations sent
              </label>
              <input
                type="number"
                placeholder="Invitations sent"
                value={form.invitations_sent}
                onChange={(e) =>
                  handleChange("invitations_sent", Number(e.target.value))
                }
                className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white"
              />
            </div>
          </div> */}

          {/* Amount + Currency */}
          {/* <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-slate-300 mb-1">
                Amount to be settled
              </label>
              <div className="grid grid-cols-2 gap-4">
                <select
                  value={form.currency}
                  onChange={(e) => handleChange("currency", e.target.value)}
                  className="bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white"
                >
                  {CURRENCIES.map((cur) => (
                    <option key={cur} value={cur}>
                      {cur}
                    </option>
                  ))}
                </select>
                <input
                  type="number"
                  placeholder="Amount"
                  value={form.amount}
                  onChange={(e) =>
                    handleChange("amount", Number(e.target.value))
                  }
                  className="bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white"
                />
              </div>
            </div>
          </div> */}

          {error && (
            <div className="bg-red-500/10 border border-red-500/20 text-red-400 text-sm px-4 py-2 rounded-lg">
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-white/10">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm rounded-lg text-slate-300 hover:bg-slate-800 transition"
          >
            Cancel
          </button>

          <button
            onClick={handleSubmit}
            disabled={loading}
            className={`px-4 py-2 text-sm rounded-lg text-white font-medium transition ${
              loading
                ? "bg-pink-400 cursor-not-allowed"
                : "bg-pink-600 hover:bg-pink-700"
            }`}
          >
            {loading
              ? "Saving..."
              : mode === "edit"
                ? "Update Event"
                : "Create Event"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EventFormModal;
